import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretDown, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import MicButton from "@/components/MicButton";

const OPTIONAL_FIELDS = [
  { key: "why_now", label: "Why is this important now?" },
  { key: "sought_outcome", label: "What outcome, decision, or transition are you seeking?" },
  { key: "tried", label: "What has already been tried?" },
  { key: "unacceptable", label: "What would make a solution unacceptable?" },
  { key: "available_evidence", label: "What information, documents, or evidence are available?" },
];

export default function NewChallenge({ sample, onSampleSubmit }) {
  const [narrative, setNarrative] = useState(sample?.narrative || "");
  const [optional, setOptional] = useState(() => {
    if (!sample) return {};
    const { narrative: _n, ...rest } = sample;
    return rest;
  });
  const [open, setOpen] = useState(!!sample);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const submit = async () => {
    if (sample) return onSampleSubmit?.();
    if (!narrative.trim()) {
      toast.error("Please describe the challenge first.");
      return;
    }
    setSubmitting(true);
    try {
      const session = await api.createSession({ narrative: narrative.trim(), ...optional });
      nav(`/sessions/${session.id}`);
    } catch (e) {
      toast.error("Could not create session", { description: e?.response?.data?.detail || e.message });
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-14">
      <div className="overline mb-3">New challenge</div>
      <h1 className="font-serif text-4xl leading-tight sm:text-5xl">Describe the challenge</h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Describe the challenge as you currently understand it. You do not need to organize it perfectly.
        The system will help examine and expand the representation before considering possible action.
      </p>

      <div data-testid="narrative-box" className="mt-8 relative">
        <textarea
          data-testid="challenge-narrative-input"
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={sample ? 6 : 12}
          readOnly={!!sample}
          autoFocus={!sample}
          placeholder="What is happening? Write freely, in your own words…"
          className="w-full resize-y rounded-sm border border-input bg-card p-5 pr-12 text-[16px] leading-relaxed outline-none transition-colors focus:border-foreground focus:ring-1 focus:ring-ring"
        />
        <MicButton size={20} className="absolute right-3 top-3" onResult={(t) => setNarrative((p) => (p ? p.trim() + " " + t : t))} />
      </div>

      <div data-testid="optional-fields-box" className="mt-4 border border-border">
        <button
          data-testid="optional-fields-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <span className="overline">Optional supporting detail</span>
          <CaretDown size={16} className="text-muted-foreground transition-transform duration-200" style={{ transform: open ? "rotate(180deg)" : "none" }} />
        </button>
        {open && (
          <div className="reveal space-y-5 border-t border-border px-5 py-5">
            {OPTIONAL_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="mb-2 block text-sm font-medium">{f.label}</label>
                <div className="relative">
                  <textarea
                    data-testid={`optional-${f.key}`}
                    value={optional[f.key] || ""}
                    onChange={(e) => setOptional((o) => ({ ...o, [f.key]: e.target.value }))}
                    rows={2}
                    readOnly={!!sample}
                    className="w-full resize-y rounded-sm border border-input bg-background p-3 pr-11 text-sm leading-relaxed outline-none transition-colors focus:border-foreground focus:ring-1 focus:ring-ring"
                  />
                  <MicButton size={16} className="absolute right-2.5 top-2.5" onResult={(t) => setOptional((o) => ({ ...o, [f.key]: (o[f.key] ? o[f.key].trim() + " " + t : t) }))} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="examine-row" className="mt-8 flex items-center justify-end">
        <button
          data-testid="examine-challenge-btn"
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-50"
        >
          {submitting ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />}
          {submitting ? "Preparing examination…" : "Examine the Challenge"}
        </button>
      </div>
    </div>
  );
}
